package co.maytes.standupbot.dashboard.features.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.motion.Disclosure
import co.maytes.standupbot.dashboard.components.motion.Presence
import co.maytes.standupbot.dashboard.components.motion.presenceClasses
import co.maytes.standupbot.dashboard.components.motion.pressable
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.svg.Svg
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgCircle
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgLine
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgPath
import co.maytes.standupbot.dashboard.styles.Elevation
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.motion.PresenceState
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.HTMLElement

private const val REPLAY_GAP_MS = 140L
private const val PRESS_HOLD_EXTRA_MS = 160L

private val curves = listOf(
    "EASE_STANDARD" to Motion.EASE_STANDARD,
    "EASE_EMPHASIS" to Motion.EASE_EMPHASIS,
    "EASE_DRAWER" to Motion.EASE_DRAWER,
)

private val durations: List<Pair<String, String>> = listOf(
    "press" to Motion.DUR_PRESS,
    "release" to Motion.DUR_RELEASE,
    "fast (hover, colour)" to Motion.DUR_FAST,
    "base" to Motion.DUR_BASE,
    "popover in" to Motion.DUR_POPOVER_IN,
    "popover out" to Motion.DUR_POPOVER_OUT,
    "modal in" to Motion.DUR_MODAL_IN,
    "modal out" to Motion.DUR_MODAL_OUT,
    "drawer in" to Motion.DUR_DRAWER_IN,
    "drawer out" to Motion.DUR_DRAWER_OUT,
    "toast in" to "${Motion.TOAST_IN_MS}ms",
    "toast out" to "${Motion.TOAST_OUT_MS}ms",
    "disclosure" to Motion.DUR_DISCLOSURE,
    "content swap" to Motion.DUR_CONTENT_SWAP,
    "segment thumb" to Motion.DUR_SEGMENT_THUMB,
    "check draw" to Motion.DUR_CHECK_DRAW,
    "enter" to Motion.ENTER_DUR,
    "chart intro" to "${Motion.CHART_INTRO_MS}ms",
)

@Composable
fun MotionSection() {
    GallerySection(
        id = "motion",
        title = "Motion",
        description = "Curves and durations come straight from the Motion tokens. Replay runs the real registered CSS, " +
            "so the reduced-motion switch in the header changes what you see.",
    ) {
        GalleryHeading("Curves")
        GalleryGrid(minColumn = 200) {
            curves.forEach { (name, curve) -> CurveCard(name, curve) }
        }
        GalleryHeading("Durations")
        GalleryGrid(minColumn = 220) {
            GalleryPanel {
                durations.take((durations.size + 1) / 2).forEach { (name, value) -> DurationRow(name, value) }
            }
            GalleryPanel {
                durations.drop((durations.size + 1) / 2).forEach { (name, value) -> DurationRow(name, value) }
            }
        }
        GalleryHeading("Replay")
        GalleryGrid(minColumn = 260) {
            PopoverDemo()
            ModalDemo()
            DrawerDemo()
            ToastDemo()
            PressDemo()
            DisclosureDemo()
        }
    }
}

@Composable
private fun DurationRow(name: String, value: String) {
    Div(
        attrs = {
            style {
                property("display", "flex")
                property("justify-content", "space-between")
                property("gap", "8px")
            }
        },
    ) {
        GalleryMono(name)
        GalleryMono(value, Palette.textPrimary)
    }
}

private fun bezierPoints(curve: String): List<Double> =
    curve.substringAfter("(").substringBefore(")").split(",").map { it.trim().toDouble() }

@Composable
private fun CurveCard(name: String, curve: String) {
    val (x1, y1, x2, y2) = bezierPoints(curve)
    val size = 100.0
    fun px(x: Double) = (x * size).toString()
    fun py(y: Double) = (size - y * size).toString()
    GalleryPanel {
        Svg(attrs = {
            attr("viewBox", "-8 -8 116 116")
            attr("width", "112")
            attr("height", "112")
            attr("role", "img")
            attr("aria-label", "$name cubic bezier plot")
        }) {
            SvgLine {
                attr("x1", "0")
                attr("y1", "100")
                attr("x2", "100")
                attr("y2", "0")
                attr("stroke", Palette.border.cssRgb())
                attr("stroke-dasharray", "3 3")
            }
            SvgLine {
                attr("x1", "0")
                attr("y1", "100")
                attr("x2", px(x1))
                attr("y2", py(y1))
                attr("stroke", Palette.textTertiary.cssRgb())
            }
            SvgLine {
                attr("x1", "100")
                attr("y1", "0")
                attr("x2", px(x2))
                attr("y2", py(y2))
                attr("stroke", Palette.textTertiary.cssRgb())
            }
            SvgPath {
                attr("d", "M0,100 C${px(x1)},${py(y1)} ${px(x2)},${py(y2)} 100,0")
                attr("fill", "none")
                attr("stroke", Palette.accent.cssRgb())
                attr("stroke-width", "2.5")
            }
            SvgCircle {
                attr("cx", px(x1))
                attr("cy", py(y1))
                attr("r", "3.5")
                attr("fill", Palette.textTertiary.cssRgb())
            }
            SvgCircle {
                attr("cx", px(x2))
                attr("cy", py(y2))
                attr("r", "3.5")
                attr("fill", Palette.textTertiary.cssRgb())
            }
        }
        GalleryHeading(name)
        GalleryMono(curve)
    }
}

@Composable
private fun DemoFrame(
    title: String,
    detail: String,
    onReplay: () -> Unit,
    stage: @Composable () -> Unit,
) {
    GalleryPanel {
        Div(
            attrs = {
                style {
                    property("display", "flex")
                    property("justify-content", "space-between")
                    property("align-items", "center")
                    property("gap", "8px")
                }
            },
        ) {
            Stack(2) {
                GalleryHeading(title)
                GalleryMono(detail)
            }
            Button(label = "Replay", variant = ButtonVariant.Secondary, size = ButtonSize.Sm, onClick = onReplay)
        }
        Div(
            attrs = {
                style {
                    property("position", "relative")
                    property("height", "148px")
                    property("overflow", "hidden")
                    property("border-radius", "8px")
                    property("background-color", Palette.surfaceSubtle.cssRgb())
                    property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
                }
            },
        ) {
            stage()
        }
    }
}

@Composable
private fun PresenceDemo(
    title: String,
    enterMs: Int,
    exitMs: Int,
    stage: @Composable (PresenceState) -> Unit,
) {
    var visible by remember { mutableStateOf(true) }
    val scope = rememberCoroutineScope()
    DemoFrame(
        title = title,
        detail = "in ${enterMs}ms · out ${exitMs}ms",
        onReplay = {
            scope.launch {
                visible = false
                delay(exitMs + REPLAY_GAP_MS)
                visible = true
            }
        },
    ) {
        Presence(visible = visible, enterMs = enterMs, exitMs = exitMs, animateInitial = false) { stage(it) }
    }
}

private fun stageSurface(): Modifier = Modifier.styleModifier {
    property("background-color", Palette.surface.cssRgb())
    property("border", "1px solid ${Palette.border.cssRgb()}")
    property("color", Palette.textPrimary.cssRgb())
    property("font-size", "${Typography.bodySmall.size}")
}

@Composable
private fun PopoverDemo() = PresenceDemo("Popover", Motion.POPOVER_IN_MS, Motion.POPOVER_OUT_MS) { presence ->
    Div(
        attrs = {
            style {
                property("position", "absolute")
                property("top", "16px")
                property("left", "16px")
                property("padding", "6px 12px")
                property("border-radius", "8px")
                property("background-color", Palette.surface.cssRgb())
                property("border", "1px solid ${Palette.border.cssRgb()}")
                property("font-size", "${Typography.bodySmall.size}")
                property("color", Palette.textPrimary.cssRgb())
            }
        },
    ) { org.jetbrains.compose.web.dom.Text("Trigger") }
    Div(
        attrs = Modifier
            .presenceClasses("sb-pop", presence)
            .then(stageSurface())
            .styleModifier {
                property("position", "absolute")
                property("top", "58px")
                property("left", "16px")
                property("transform-origin", "top left")
                property("padding", "10px 14px")
                property("border-radius", "12px")
                property("box-shadow", Elevation.shadow(2))
            }
            .toAttrs(),
    ) { org.jetbrains.compose.web.dom.Text("Menu item one") }
}

@Composable
private fun ModalDemo() = PresenceDemo("Modal", Motion.MODAL_IN_MS, Motion.MODAL_OUT_MS) { presence ->
    Div(
        attrs = Modifier
            .presenceClasses("sb-scrim", presence)
            .styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("background-color", "rgba(0, 0, 0, 0.45)")
            }
            .toAttrs(),
    )
    Div(
        attrs = Modifier
            .presenceClasses("sb-dialog", presence)
            .then(stageSurface())
            .styleModifier {
                property("position", "absolute")
                property("top", "50%")
                property("left", "50%")
                property("margin", "-34px 0 0 -90px")
                property("width", "180px")
                property("padding", "16px")
                property("border-radius", "12px")
                property("box-shadow", Elevation.shadow(3))
                property("text-align", "center")
            }
            .toAttrs(),
    ) { org.jetbrains.compose.web.dom.Text("Dialog") }
}

@Composable
private fun DrawerDemo() = PresenceDemo("Drawer", Motion.DRAWER_IN_MS, Motion.DRAWER_OUT_MS) { presence ->
    Div(
        attrs = Modifier
            .presenceClasses("sb-drawer-scrim", presence)
            .styleModifier {
                property("position", "absolute")
                property("inset", "0")
                property("background-color", "rgba(0, 0, 0, 0.45)")
            }
            .toAttrs(),
    )
    Div(
        attrs = Modifier
            .presenceClasses("sb-drawer", presence)
            .then(stageSurface())
            .styleModifier {
                property("position", "absolute")
                property("top", "0")
                property("bottom", "0")
                property("left", "0")
                property("width", "132px")
                property("padding", "16px")
            }
            .toAttrs(),
    ) { org.jetbrains.compose.web.dom.Text("Navigation") }
}

@Composable
private fun ToastDemo() = PresenceDemo("Toast", Motion.TOAST_IN_MS, Motion.TOAST_OUT_MS) { presence ->
    Div(
        attrs = Modifier
            .presenceClasses("sb-toast", presence)
            .styleModifier {
                property("position", "absolute")
                property("bottom", "16px")
                property("left", "50%")
                property("padding", "10px 16px")
                property("border-radius", "8px")
                property("white-space", "nowrap")
                property("background-color", Palette.primary.cssRgb())
                property("color", Palette.onPrimary.cssRgb())
                property("font-size", "${Typography.bodySmall.size}")
            }
            .toAttrs(),
    ) { org.jetbrains.compose.web.dom.Text("Saved changes") }
}

@Composable
private fun PressDemo() {
    val scope = rememberCoroutineScope()
    val holder = remember { PressTarget() }
    DemoFrame(
        title = "Press",
        detail = "down ${Motion.PRESS_MS}ms · release ${Motion.RELEASE_MS}ms · scale ${Motion.PRESS_SCALE}",
        onReplay = {
            scope.launch {
                holder.element?.setAttribute(ForcedStates.ATTRIBUTE, "active")
                delay(Motion.PRESS_MS + PRESS_HOLD_EXTRA_MS)
                holder.element?.removeAttribute(ForcedStates.ATTRIBUTE)
            }
        },
    ) {
        Div(
            attrs = Modifier
                .pressable()
                .styleModifier {
                    property("position", "absolute")
                    property("top", "50%")
                    property("left", "50%")
                    property("margin", "-22px 0 0 -70px")
                    property("width", "140px")
                    property("height", "44px")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("border-radius", "10px")
                    property("background-color", Palette.surface.cssRgb())
                    property("border", "1px solid ${Palette.border.cssRgb()}")
                    property("color", Palette.textPrimary.cssRgb())
                    property("font-size", "${Typography.bodySmall.size}")
                    property("cursor", "pointer")
                }
                .toAttrs {
                    ref { element ->
                        holder.element = element
                        onDispose { holder.element = null }
                    }
                },
        ) { org.jetbrains.compose.web.dom.Text("Press me") }
    }
}

private class PressTarget(var element: HTMLElement? = null)

@Composable
private fun DisclosureDemo() {
    var open by remember { mutableStateOf(true) }
    val scope = rememberCoroutineScope()
    DemoFrame(
        title = "Disclosure",
        detail = "${Motion.DISCLOSURE_MS}ms · grid-template-rows + opacity",
        onReplay = {
            scope.launch {
                open = false
                delay(Motion.DISCLOSURE_MS + REPLAY_GAP_MS)
                open = true
            }
        },
    ) {
        Stack(gap = 8, modifier = Modifier.styleModifier { property("padding", "16px") }) {
            SpanText(
                text = "Advanced settings",
                modifier = Modifier.styleModifier {
                    property("font-weight", "600")
                    property("font-size", "${Typography.label.size}")
                    property("color", Palette.textPrimary.cssRgb())
                },
            )
            Disclosure(open = open) {
                SpanText(
                    text = "Content slides open by animating the grid track from 0fr to 1fr, " +
                        "so no height is measured in script.",
                    modifier = Modifier.styleModifier {
                        property("font-size", "${Typography.bodySmall.size}")
                        property("color", Palette.textSecondary.cssRgb())
                    },
                )
            }
        }
    }
}
